import { createSlice } from '@reduxjs/toolkit';
import copy from 'fast-copy';
import moment from 'moment';
import { CostGroup, CustomCost, ExchangeData } from 'pdf-generator';
import { BOMLevelType, ReportConfig, ReportType } from '../../../components/pdfBuilder/types';
import { Association, SelectBoxOption } from '../../../interfaces';
import { AccessPayload } from '../../services';

export interface PricingBasicDetails {
    pricing_name: string,
    remarks: string,
    bom_id: SelectBoxOption | null,
    pricing_model: SelectBoxOption | null,
    object_type: SelectBoxOption | null,
    object_id: SelectBoxOption | null,
    priority: SelectBoxOption | null,
}

interface PricingBasicDetailsError {
    pricing_name: boolean,
    remarks: boolean,
    bom_id: boolean,
    pricing_model: boolean,
    object_type: boolean,
    object_id: boolean,
    priority: boolean,
}

export interface PricingSliceInterface {
    // Basic Details
    basic_details: PricingBasicDetails,

    id: string,
    sequenceId?: string,
    version?: number,
    bom_cost: any,
    cost_groups: CostGroup[],
    margin_ratio: number,
    discounts: CustomCost[],
    taxes: CustomCost[],
    other_costs: CustomCost[],
    exchange_rates: ExchangeData,
    bom_cost_quantity: number,
    bom_cost_discount_percentage?: number,
    modified_time: Date;
    released?: boolean;
    report_config?: Partial<ReportConfig>;
    associations: Association[];
    serial_num?: string;

    error: {
        // Basic Details
        basic_details: PricingBasicDetailsError,
    }
}

export const levelOptions = [
    { label: "1st Level Breakup", value: "1stLevelbreakup" },
    { label: "All Level Breakup", value: "AllLevelbreakup" },
    { label: "No Breakup", value: "Nobreakup" },
];

export const reportTypeOptions = [
    { label: "Commercial Only", value: "Commercial Only" },
    { label: "Technical Only", value: "Technical Only" },
    { label: "Techno Commercial", value: "Techno Commercial" }
];

const initialState: PricingSliceInterface = {
    basic_details: { pricing_name: "", remarks: "", bom_id: null, pricing_model: null, object_type: null, object_id: null, priority: null },
    sequenceId: "...",
    id: "New",
    bom_cost: {},
    cost_groups: [],
    discounts: [],
    taxes: [],
    other_costs: [],
    exchange_rates: {
        base_currency: "",
        current_date: moment(),
        exchange_date: moment(),
        manual_rate: false,
        exchange_rates_auto: [],
        exchange_rates_manual: [],
        exchange_rates: [],
    },
    bom_cost_quantity: 1,
    bom_cost_discount_percentage: 0,
    margin_ratio: 0,
    modified_time: new Date(),
    released: false,
    report_config: {
        include_millions_formatting: false,
        table_header_color: '#d1c4e9',
        bom_level: levelOptions[0].value as BOMLevelType,
        report_type: reportTypeOptions[2].value as ReportType,
        include_other_costs: false,
        include_adjustments: false,
        include_tax: false,
        only_bom: true,
        include_notes: true,
        normalize_cost: false,
        // hasCostGroupItems: false
    },
    associations: [],
    serial_num: undefined,
    error: {
        basic_details: { pricing_name: false, remarks: false, bom_id: false, pricing_model: false, object_type: false, object_id: false, priority: false }
    }
}

export const pricingSlice = createSlice({
    name: 'pricing',
    initialState,
    reducers: {
        // To Slice by key value pair
        updatePricingSliceByKeyValue(state, { payload }: { payload: { parentKey: string, childKey: string, value: any } }) {
            let newState: any = copy(state);
            newState.error[payload.parentKey][payload.childKey] = payload.value ? false : true;
            newState[payload.parentKey][payload.childKey] = payload.value;
            return { ...state, ...newState }
        },

        // To update errors
        updateErrors(state, { payload }: { payload: { parentKey: keyof PricingSliceInterface, childKey: keyof PricingBasicDetails }[] }) {
            let product: PricingSliceInterface = copy(state);
            let error = product.error;
            payload.forEach((key) => {
                if (key.parentKey === 'basic_details') {
                    if (!product.basic_details[key.childKey as keyof PricingBasicDetails]) {
                        error.basic_details[key.childKey as keyof PricingBasicDetails] = true;
                    }
                }
            })

            return { ...state, error }
        },

        // To reset all values to initialValue
        resetPricingSlice(state) {
            return { ...state, ...initialState }
        },

        // To Bulk Update the slice
        updatePricingSlice(state, { payload }: {
            payload: {
                // Basic Details
                basic_details: PricingBasicDetails,
                id: string,
                sequenceId: string,
                version: number,
                user_access: AccessPayload,
                bom_cost: any,
                cost_groups: CostGroup[],
                discounts: CustomCost[],
                taxes: CustomCost[],
                other_costs: CustomCost[],
                exchange_rates: ExchangeData,
                bom_cost_quantity: number,
                bom_cost_discount_percentage: number,
                margin_ratio: number,
                modified_time: Date,
                released?: boolean,
                report_config?: Partial<ReportConfig>;
                associations: Association[];
                serial_num?: string
            }
        }) {
            return { ...state, ...payload }
        },

        // To Bulk update error
        updateErrorObj(state, { payload }) {
            return { ...state, error: payload }
        },

        // To Slice by key value pair
        updatePricingByKeyValue(state, { payload }: { payload: { key: keyof PricingSliceInterface, value: any } }) {
            let newState: any = copy(state);
            newState[payload.key] = payload.value;
            return { ...state, ...newState }
        },
    }
});

export const { updatePricingSliceByKeyValue, updatePricingSlice, resetPricingSlice, updateErrors, updateErrorObj, updatePricingByKeyValue } = pricingSlice.actions;
export default pricingSlice.reducer;